// P1-006: Hardware Manager — business logic for buy/install/uninstall/sell
import { useHardwareStore } from '../stores/hardwareStore';
import { usePlayerStore } from '@/shared/stores/playerStore';
import { HARDWARE_CONFIGS, createHardwareItem } from '../data/hardware-configs';
import { applyInflation } from '@/shared/formulas/economy';

export interface HardwareActionResult {
  success: boolean;
  error?: string;
}

export const HardwareManager = {
  /**
   * Buy a hardware item from the shop catalog.
   * Deducts FLOP, adds item to inventory.
   */
  buyHardware(configId: string): HardwareActionResult {
    const config = HARDWARE_CONFIGS[configId];
    if (!config) {
      return { success: false, error: `Unknown hardware: ${configId}` };
    }

    const playerStore = usePlayerStore.getState();
    const actualPrice = applyInflation(config.price, playerStore.completedProjects);
    if (!playerStore.spendFlop(actualPrice)) {
      return { success: false, error: `Insufficient FLOP (need ${actualPrice}₣)` };
    }

    const item = createHardwareItem(configId);
    useHardwareStore.getState().addToInventory(item);
    return { success: true };
  },

  /**
   * Install a hardware item from inventory into a specific slot.
   */
  installToSlot(instanceId: string, slotIndex: number): HardwareActionResult {
    const hwStore = useHardwareStore.getState();
    const playerStore = usePlayerStore.getState();

    // Check slot is within allowed range
    if (slotIndex < 0 || slotIndex >= playerStore.hardwareSlots) {
      return { success: false, error: `Slot ${slotIndex} is out of range (max ${playerStore.hardwareSlots - 1})` };
    }

    const item = hwStore.inventory.find((i) => i.instanceId === instanceId);
    if (!item) {
      return { success: false, error: `Item ${instanceId} not found in inventory` };
    }

    // Check if slot exists in store (may need to extend slots array)
    const slot = hwStore.slots[slotIndex];
    if (!slot) {
      return { success: false, error: `Slot ${slotIndex} does not exist` };
    }

    if (slot.installed !== null) {
      return { success: false, error: `Slot ${slotIndex} is occupied` };
    }

    const ok = hwStore.installHardware(slotIndex, item);
    if (!ok) {
      return { success: false, error: 'Install failed' };
    }
    return { success: true };
  },

  /**
   * Uninstall hardware from a slot back to inventory.
   */
  uninstallFromSlot(slotIndex: number): HardwareActionResult {
    const hwStore = useHardwareStore.getState();
    const slot = hwStore.slots[slotIndex];
    if (!slot) {
      return { success: false, error: `Slot ${slotIndex} does not exist` };
    }
    if (slot.installed === null) {
      return { success: false, error: `Slot ${slotIndex} is empty` };
    }

    const item = hwStore.uninstallHardware(slotIndex);
    if (!item) {
      return { success: false, error: 'Uninstall failed' };
    }
    return { success: true };
  },

  /**
   * Sell a hardware item from inventory. Returns 50% of original price in FLOP.
   */
  sellHardware(instanceId: string): HardwareActionResult & { resellPrice?: number } {
    const hwStore = useHardwareStore.getState();
    const item = hwStore.inventory.find((i) => i.instanceId === instanceId);
    if (!item) {
      return { success: false, error: `Item ${instanceId} not found in inventory` };
    }

    const resellPrice = hwStore.sellHardware(instanceId);
    if (resellPrice > 0) {
      usePlayerStore.getState().addFlop(resellPrice);
    }
    return { success: true, resellPrice };
  },
};
